import { useCallback, useRef } from 'react';
import type { ApiResponse, PublicSpaceMessage, SendSpaceMessageParams } from '@adieuu/shared';
import type { SpaceChannelMessagesState } from './types';

type SpacesSendApiLike = {
  sendMessage: (
    spaceId: string,
    channelId: string,
    params: SendSpaceMessageParams,
  ) => Promise<ApiResponse<PublicSpaceMessage>>;
};

export interface SpaceSendParams {
  api: { spaces: SpacesSendApiLike };
  activeSpaceIdRef: React.MutableRefObject<string | null>;
  activeChannelIdRef: React.MutableRefObject<string | null>;
  setSending: React.Dispatch<React.SetStateAction<boolean>>;
  setMessagesByChannel: React.Dispatch<React.SetStateAction<Record<string, SpaceChannelMessagesState>>>;
  showError?: (message: string) => void;
}

export function useSpaceSend(params: SpaceSendParams) {
  const { api, activeSpaceIdRef, activeChannelIdRef, setSending, setMessagesByChannel, showError } = params;
  const sendingRef = useRef(false);

  const sendMessage = useCallback(
    async (
      msgParams: SendSpaceMessageParams,
      queued?: { spaceId: string; channelId: string },
    ): Promise<PublicSpaceMessage | null> => {
      const spaceId = queued?.spaceId ?? activeSpaceIdRef.current;
      const channelId = queued?.channelId ?? activeChannelIdRef.current;
      if (!spaceId || !channelId) return null;
      if (!queued) {
        if (sendingRef.current) {
          showError?.('A message is already sending.');
          return null;
        }
        sendingRef.current = true;
        setSending(true);
      }
      try {
        const clientMessageId = msgParams.clientMessageId ?? crypto.randomUUID();
        const res = await api.spaces.sendMessage(spaceId, channelId, {
          ...msgParams,
          clientMessageId,
        });
        if (res.success && res.data) {
          if (res.data.threadOnly) return res.data;
          setMessagesByChannel((prev) => {
            const state = prev[channelId];
            if (!state) return prev;
            if (state.messages.some((m) => m.id === res.data!.id)) return prev;
            return {
              ...prev,
              [channelId]: {
                ...state,
                messages: [res.data!, ...state.messages],
              },
            };
          });
          return res.data;
        }
        // Queued sends report failure on their pending row instead.
        if (showError && !queued) {
          showError(res.error?.message ?? 'Failed to send message.');
        }
        return null;
      } finally {
        if (!queued) {
          sendingRef.current = false;
          setSending(false);
        }
      }
    },
    [api, activeSpaceIdRef, activeChannelIdRef, setSending, setMessagesByChannel, showError],
  );

  return { sendMessage };
}
